import { Button, Typography } from "@mui/material";
import React, { Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LoadingOrError from "shared-ui/src/global-layout/LoadingOrError";
import { MicrosoftInsightsHowToUse } from "../../../../shared-features/report-center/microsoft-insights/components/MicrosoftInsightsHowToUse";
import PageHeader from "@containers/PageHeader";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import { useClaims } from "../../../../../../auth/ClaimsProvider";

const MicrosoftInsights = React.lazy(
  () => import("../../../../shared-features/report-center/microsoft-insights/MicrosoftInsights"),
);

const MicrosoftInsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { prefix } = useParams<{ prefix?: string }>();
  const { hasPermission, claimsLoading } = useClaims();
  const hasAccess = hasPermission(ShiOnePermission.microsoftInsightsRead);

  /** Boolean flag to track the open state of the "How to Use" dialog. */
  const [howToUseOpen, setHowToUseOpen] = useState(false);

  useEffect(() => {
    if (!claimsLoading && !hasAccess) {
      navigate(prefix ? `/${ prefix }/assessments` : "/assessments", { replace: true });
    }
  }, [claimsLoading, hasAccess, navigate, prefix]);

  if (claimsLoading) return <LoadingOrError loading />;
  if (!hasAccess) return null;

  /** Handler to open the "How to Use" dialog. */
  function handleOpenHowToUse(): void { setHowToUseOpen(true); }

  /** Handler to close the "How to Use" dialog. */
  function handleCloseHowToUse(): void { setHowToUseOpen(false);}

  return (
    <>
      <PageHeader
        header={"Microsoft Insights"}
        subheader={"Feature level usage reporting"}
        icon={
          <AssessmentIcon
              aria-hidden="true"
              focusable="false"
              sx={{ fontSize: "2.3rem", color: "text.secondary" }}
            />
        }
        headerComponents={
          <>
            <Button
              size="small"
              onClick={handleOpenHowToUse}
              sx={{
                textTransform: "none",
                fontSize: 14,
                color: "primary.main",
                fontWeight: 600,
                p: 0,
                minWidth: 0,
                gap: 0.75,
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
              }}
              disableRipple
            >
              <Typography component="span" variant="inherit">How to use this report</Typography>
            </Button>
            <MicrosoftInsightsHowToUse
              open={howToUseOpen}
              onClose={handleCloseHowToUse}
            />
          </>
        }
        backButtonUrl={"/assessments"}
      />
      <Suspense fallback={<LoadingOrError loading />}>
        <MicrosoftInsights />
      </Suspense>
    </>
  );
};

export default MicrosoftInsightsPage;
